import React, { useState, useEffect } from 'react';
import { logError } from '../lib/logger';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { Mail, Phone, MapPin, Send } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Contact = ({ initialFormType = null }) => {
  const [formType, setFormType] = useState(initialFormType);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    companyDescription: '',
    phone: '',
    message: '',
    mailingListOptIn: false
  });

  useEffect(() => {
    if (initialFormType) {
      setFormType(initialFormType);
    }
  }, [initialFormType]);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/contact/submit`, {
        form_type: formType,
        name: formData.name,
        email: formData.email,
        company: formData.company,
        company_description: formData.companyDescription,
        phone: formData.phone,
        message: formData.message,
        mailing_list_opt_in: formData.mailingListOptIn
      });

      if (response.data.success) {
        toast.success(response.data.message || `Thank you! We'll contact you shortly regarding your ${formType.toLowerCase()} request.`);
        
        setFormData({
          name: '',
          email: '',
          company: '',
          companyDescription: '',
          phone: '',
          message: '',
          mailingListOptIn: false
        });
        setFormType(null);
      } else {
        toast.error('Something went wrong. Please try again.');
      }
    } catch (error) {
      logError('Form submission error:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to submit form. Please try again or contact us directly at info@ctrlsys.com';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-14 sm:py-20 md:py-24 bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10 sm:mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
            Get in <span className="text-blue-600">Touch</span>
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-blue-600 to-orange-500 mx-auto mb-4 sm:mb-6"></div>
          <p className="max-w-3xl mx-auto text-base sm:text-lg md:text-xl text-slate-600 leading-relaxed">
            Ready to revolutionize your aviation maintenance operations? Contact us today for a demo or quote.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Contact Information */}
          <div className="space-y-8">
            <div>
              <h3 className="text-3xl font-bold text-[#1e2d3b] mb-6">Contact Information</h3>
              <p className="text-lg text-slate-600 leading-relaxed mb-8">
                Our team of aviation diagnostics experts is ready to help you find the perfect solution for your maintenance operations.
              </p>
            </div>

            <div className="space-y-6">
              <div className="flex items-start space-x-4 p-4 bg-white rounded-2xl border-2 border-slate-100 hover:border-blue-500 transition-colors duration-300">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Mail className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-[#1e2d3b] mb-2">Email</h4>
                  <div className="space-y-1">
                    <div>
                      <span className="text-sm text-slate-500">General inquiries:</span>
                      <a href="mailto:info@ctrlsys.com" className="block text-blue-600 hover:text-blue-700 transition-colors duration-200">
                        info@ctrlsys.com
                      </a>
                    </div>
                    <div>
                      <span className="text-sm text-slate-500">Sales:</span>
                      <a href="mailto:sales@ctrlsys.com" className="block text-blue-600 hover:text-blue-700 transition-colors duration-200">
                        sales@ctrlsys.com
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-start space-x-4 p-4 bg-white rounded-2xl border-2 border-slate-100 hover:border-blue-500 transition-colors duration-300">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Phone className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-[#1e2d3b] mb-1">Phone</h4>
                  <a href="tel:+14108765676" className="text-slate-600 hover:text-blue-600 transition-colors duration-200">
                    +1 (410) 876-5676
                  </a>
                </div>
              </div>

              <div className="flex items-start space-x-4 p-4 bg-white rounded-2xl border-2 border-slate-100 hover:border-blue-500 transition-colors duration-300">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <MapPin className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-[#1e2d3b] mb-1">Location</h4>
                  <p className="text-slate-600">
                    1004 Littlestown Pike, Suite H<br />
                    Westminster, MD 21157
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white rounded-3xl p-8 border-2 border-slate-100 shadow-xl">
            {!formType ? (
              <div className="space-y-4">
                <h3 className="text-2xl font-bold text-[#1e2d3b] mb-4 text-center">How can we help you?</h3>
                <div className="flex justify-center mb-4">
                  <img
                    src="/images/img_0323.png"
                    alt="ctrl.aero aviation mascot"
                    className="h-28 sm:h-36 w-auto object-contain"
                  />
                </div>
                <Button
                  onClick={() => setFormType('Request Demo')}
                  className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white text-[15px] font-semibold transition-all duration-300 hover:scale-105"
                >
                  Request Demo
                </Button>
                <Button
                  onClick={() => setFormType('Request Quote')}
                  className="w-full h-12 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white text-[15px] font-semibold transition-all duration-300 hover:scale-105"
                >
                  Request Quote
                </Button>
                <p className="text-center text-slate-500 text-sm pt-4">
                  Or use the form below for general inquiries
                </p>
                <Button
                  onClick={() => setFormType('General Inquiry')}
                  variant="outline"
                  className="w-full border-slate-300 hover:bg-slate-50"
                >
                  General Inquiry
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-2xl font-bold text-[#1e2d3b]">{formType}</h3>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setFormType(null)}
                    className="text-slate-500 hover:text-slate-700"
                  >
                    Change
                  </Button>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    data-testid="contact-form-name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder="John Doe"
                    className="h-12"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email Address *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    data-testid="contact-form-email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    placeholder="john@airline.com"
                    className="h-12"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="company">Company / Organization *</Label>
                  <Input
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    required
                    placeholder="Your Aviation Company"
                    className="h-12"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="companyDescription">Company Description *</Label>
                  <select
                    id="companyDescription"
                    name="companyDescription"
                    data-testid="contact-form-company-description"
                    value={formData.companyDescription}
                    onChange={handleInputChange}
                    required
                    className="w-full h-12 px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                  >
                    <option value="">Select company type...</option>
                    <option value="airline-operator">Airline/Operator</option>
                    <option value="aircraft-mro">Aircraft MRO</option>
                    <option value="aircraft-oem">Aircraft OEM</option>
                    <option value="component-oem">Component OEM</option>
                    <option value="component-repair">Component Repair/Overhaul Shop</option>
                    <option value="fbo">FBO</option>
                    <option value="line-maintenance">Line Maintenance Provider</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="+1 (555) 123-4567"
                    className="h-12"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message">Message</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Please tell us how you would like to deploy ultrasound in your maintenance operations. Use this space to include any questions we can answer for you when we follow up."
                    rows={5}
                    className="resize-none"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  data-testid="contact-form-submit"
                  className="w-full h-12 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                  {!isSubmitting && <Send className="ml-2 h-4 w-4" />}
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
